import React from 'react'
import type { Block } from '../../types'
import type { ImageFrame } from '../../../renderer/blocks/ImageBlock'
import { PropertyLabel, PropertyInput } from './shared'
import { Monitor, Smartphone, Laptop, Tablet, ImageIcon, Camera } from 'lucide-react'

interface ImagePropertiesProps {
  block: Block
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

const IMAGE_FRAMES: { frame: ImageFrame; label: string; icon: React.ReactNode }[] = [
  { frame: 'none', label: 'None', icon: <ImageIcon className="h-4 w-4" /> },
  { frame: 'browser', label: 'Browser', icon: <Monitor className="h-4 w-4" /> },
  { frame: 'iphone', label: 'iPhone', icon: <Smartphone className="h-4 w-4" /> },
  { frame: 'ipad', label: 'iPad', icon: <Tablet className="h-4 w-4" /> },
  { frame: 'android', label: 'Android', icon: <Smartphone className="h-4 w-4" /> },
  { frame: 'laptop', label: 'Laptop', icon: <Laptop className="h-4 w-4" /> },
  { frame: 'polaroid', label: 'Polaroid', icon: <Camera className="h-4 w-4" /> },
]

export default function ImageProperties({ block, onUpdateBlock }: ImagePropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const currentFrame = (config.frame as ImageFrame) || 'none'

  const updateConfig = (updates: Record<string, unknown>) => {
    onUpdateBlock(block.id, { config: { ...config, ...updates } })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <PropertyLabel>Image URL</PropertyLabel>
        <PropertyInput
          value={(config.url as string) ?? ''}
          onChange={(e) => updateConfig({ url: e.target.value })}
          placeholder="https://..."
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>Frame</PropertyLabel>
        <div className="grid grid-cols-3 gap-1.5">
          {IMAGE_FRAMES.map((f) => (
            <button
              key={f.frame}
              onClick={() => updateConfig({ frame: f.frame })}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all text-xs font-medium ${
                currentFrame === f.frame
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {f.icon}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Alt Text</PropertyLabel>
        <PropertyInput
          value={(config.alt as string) ?? ''}
          onChange={(e) => updateConfig({ alt: e.target.value })}
          placeholder="Describe the image"
        />
      </div>
    </div>
  )
}
